// src/components/reporting/cohort/CohortChatPanel.js
// Left-hand chat panel for the Cohort Chat Workspace (CohortSummaryPage's
// 'report' state). Owns conversation resolution against the entry-gate route
// and message state; rendering is delegated to the same generic pieces every
// other chat surface uses (MessageList, ChatInput, VoiceChatProvider).
//
// Not built on top of ChatView.js: that component needs conversationId as a
// prop before mount (for VoiceChatProvider) and always renders a ChatHeader —
// neither fits here, since conversationId isn't known until the entry-gate
// call resolves and this panel has no title/type header of its own. So this
// panel only mounts VoiceChatProvider/MessageList/ChatInput once resolution
// completes, reusing ChatView's handleSendMessage logic directly.
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayout } from '../../../contexts/LayoutContext';
import { VoiceChatProvider, useVoiceChatContext } from '../../../contexts/VoiceChatContext';
import apiService from '../../../services/apiService';
import MessageList from '../../assessment/MessageList';
import ChatInput from '../../assessment/ChatInput';
import '../../../styles/chat.css';

const ChatPanelInner = ({ conversationId, initialMessages }) => {
  const { user } = useAuth();
  const voiceChat = useVoiceChatContext();
  const [messages, setMessages] = useState(initialMessages);
  const [isThinking, setIsThinking] = useState(false);

  useEffect(() => {
    voiceChat.connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const handleSendMessage = async (messageText, { isVoice = false } = {}) => {
    const userMessage = {
      id: Date.now().toString(),
      content: messageText,
      role: 'user',
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsThinking(true);

    try {
      const response = await apiService.post(
        `/api/v1/conversations/${conversationId}/messages`,
        { message: messageText },
        user
      );

      if (response?.success && response?.data) {
        const aiResponseText =
          response.data?.aiResponse ||
          response.aiResponse ||
          response.response ||
          response.message ||
          response.data?.response ||
          response.data?.message;

        if (!aiResponseText) throw new Error('No AI response received');

        const aiMessage = {
          id: (Date.now() + 1).toString(),
          content: aiResponseText,
          role: 'ai',
          sender: 'ai',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, aiMessage]);
        setIsThinking(false);

        if (isVoice && voiceChat?.generateSpeech) {
          try {
            await voiceChat.generateSpeech(aiResponseText);
          } catch (err) {
            console.error('[CohortChatPanel] Error generating speech:', err);
          }
        }
      } else {
        setIsThinking(false);
      }
    } catch (err) {
      console.error('[CohortChatPanel] Send error:', err);
      setIsThinking(false);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I had trouble processing your message. Please try again.',
        role: 'ai',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    }
  };

  return (
    <div className="chat-view">
      <MessageList messages={messages} isThinking={isThinking} />
      <ChatInput onSendMessage={handleSendMessage} />
    </div>
  );
};

ChatPanelInner.propTypes = {
  conversationId: PropTypes.string.isRequired,
  initialMessages: PropTypes.array.isRequired,
};

// entryPath: the report's resume-or-create chat route. Defaults to the Cohort
// Real Summary's, so existing callers are unchanged; Cohort Themes passes its
// own. entryBody is that route's request body (a combined Cohort Themes report
// sends its cohort_ids); it defaults to empty. Everything after the entry gate
// is report-agnostic.
const CohortChatPanel = ({ cohortId, onConversationResolved, entryPath, entryBody }) => {
  const { user } = useAuth();
  const { collapseNav } = useLayout();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [conversationId, setConversationId] = useState(null);
  const [initialMessages, setInitialMessages] = useState([]);

  // Chat gets the full width the moment this panel is activated — same
  // transient collapse used when starting any other conversation.
  useEffect(() => { collapseNav(); }, [collapseNav]);

  const resolveConversation = useCallback(async () => {
    setStatus('loading');
    try {
      const path = entryPath || `/api/v1/analytics/cohort-real-summary/${cohortId}/chat`;
      const resp = await apiService.post(path, entryBody || {}, user);
      if (!resp?.success || !resp.data?.conversationId) {
        throw new Error('Invalid entry-gate response');
      }
      const { conversationId: id, resumed, welcomeMessage, title } = resp.data;

      if (resumed) {
        const ctx = await apiService.get(`/api/v1/conversations/${id}/context`, user);
        const conversationData = ctx?.data?.conversation || (ctx?.data?.id ? ctx.data : null);
        const msgList = conversationData?.messages?.turns || conversationData?.messages || [];
        setInitialMessages(msgList.map((msg) => ({
          id: msg.id,
          content: msg.content || '',
          role: msg.sender,
          timestamp: msg.timestamp,
          sender: msg.sender,
          messageOrder: msg.messageOrder,
        })));
      } else {
        setInitialMessages([{
          id: 'welcome',
          content: welcomeMessage || '',
          role: 'ai',
          sender: 'ai',
          timestamp: new Date().toISOString(),
        }]);
      }

      setConversationId(id);
      setStatus('ready');
      onConversationResolved?.(id, title);
    } catch (err) {
      console.error('[CohortChatPanel] Failed to resolve conversation:', err);
      setStatus('error');
    }
  }, [cohortId, entryPath, entryBody, user, onConversationResolved]);

  // Guard against StrictMode's dev-mode double-invocation of mount effects
  // (render → effect → simulated cleanup → effect again, synchronously,
  // before the first call's request even resolves). This isn't a pure GET —
  // the "not found" branch creates a conversation — so a naive
  // useEffect(() => resolveConversation(), [resolveConversation]) fires the
  // POST twice and both land in "not found" before either commits its
  // conversation_context_refs row, creating two conversations. A ref-based
  // "only the real first mount call fires" guard fixes that; Retry still
  // calls resolveConversation directly, bypassing this guard.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    resolveConversation();
  }, [resolveConversation]);

  if (status === 'loading') {
    return <div className="cs-chat-status">Loading conversation…</div>;
  }

  if (status === 'error') {
    return (
      <div className="cs-chat-status">
        <p>This conversation couldn&apos;t be loaded.</p>
        <button type="button" className="arp-btn arp-btn-secondary" onClick={resolveConversation}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <VoiceChatProvider conversationId={conversationId}>
      <ChatPanelInner conversationId={conversationId} initialMessages={initialMessages} />
    </VoiceChatProvider>
  );
};

// cohortId is only needed for the default (Cohort Real Summary) entry path.
CohortChatPanel.propTypes = {
  cohortId: PropTypes.string,
  onConversationResolved: PropTypes.func,
  entryPath: PropTypes.string,
  entryBody: PropTypes.object,
};

export default CohortChatPanel;
